import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import getEnv from '../utils/getEnv';
import { generateUrl } from '../utils/Routing';

import type {
  SigninRedirectArgs,
  SignoutRedirectArgs,
  User,
  UserManagerSettings,
} from 'oidc-client-ts';

const useOidcStore = defineStore(
  'oidc',
  () => {
    const user = ref<User | null>(null);
    const isLoading = ref<boolean>(false);
    const error = ref<Error | null>(null);

    const authority = `${window.location.protocol}//${window.location.host}`;
    const clientId = getEnv('oauth_client_id') ?? '';
    const redirectUri = `${window.location.protocol}//${window.location.host}${generateUrl('customer_oidc_callback')}`;

    const settings: UserManagerSettings = {
      authority,
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid profile webshop',
      loadUserInfo: false,
      automaticSilentRenew: false,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
    };

    const userManager = new UserManager(settings);

    const storageKey = `oidc.user:${authority}:${clientId}`;

    async function syncUserFromStorage() {
      try {
        const storedUser = await userManager.getUser();
        if (storedUser && !storedUser.expired) {
          user.value = storedUser;
        } else {
          user.value = null;
        }
      } catch {
        user.value = null;
      }
    }

    function setupStorageListener() {
      window.addEventListener('storage', event => {
        if (event.key === storageKey) {
          syncUserFromStorage();
        }
      });

      window.addEventListener('focus', () => {
        syncUserFromStorage();
      });
    }

    if (typeof window !== 'undefined') {
      setupStorageListener();
    }

    const oidcIsAuthenticated = computed(() => user.value !== null && !user.value.expired);

    const oidcAccessToken = computed(() => user.value?.access_token ?? null);

    const oidcIdToken = computed(() => user.value?.id_token ?? null);

    async function signinRedirect(args?: SigninRedirectArgs) {
      try {
        isLoading.value = true;
        error.value = null;
        await userManager.signinRedirect(args);
      } catch (err) {
        error.value = err instanceof Error ? err : new Error('Sign-in redirect failed');
        throw err;
      } finally {
        isLoading.value = false;
      }
    }

    async function signinRedirectCallback(url?: string) {
      try {
        isLoading.value = true;
        error.value = null;
        const authenticatedUser = await userManager.signinRedirectCallback(url);
        user.value = authenticatedUser;
        return authenticatedUser;
      } catch (err) {
        error.value = err instanceof Error ? err : new Error('Sign-in callback failed');
        throw err;
      } finally {
        isLoading.value = false;
      }
    }

    async function signoutRedirect(args?: SignoutRedirectArgs) {
      try {
        isLoading.value = true;
        error.value = null;
        await userManager.signoutRedirect(args);
      } catch (err) {
        error.value = err instanceof Error ? err : new Error('Sign-out redirect failed');
        throw err;
      } finally {
        isLoading.value = false;
      }
    }

    async function signoutRedirectCallback(url?: string) {
      try {
        isLoading.value = true;
        error.value = null;
        await userManager.signoutRedirectCallback(url);
        user.value = null;
      } catch (err) {
        error.value = err instanceof Error ? err : new Error('Sign-out callback failed');
        throw err;
      } finally {
        isLoading.value = false;
      }
    }

    async function getUser() {
      try {
        isLoading.value = true;
        error.value = null;
        const currentUser = await userManager.getUser();
        user.value = currentUser;
        return currentUser;
      } catch (err) {
        error.value = err instanceof Error ? err : new Error('Get user failed');
        throw err;
      } finally {
        isLoading.value = false;
      }
    }

    async function removeUser() {
      try {
        isLoading.value = true;
        error.value = null;
        await userManager.removeUser();
        user.value = null;
      } catch (err) {
        error.value = err instanceof Error ? err : new Error('Remove user failed');
        throw err;
      } finally {
        isLoading.value = false;
      }
    }

    async function checkAccess(_fullPath?: string) {
      try {
        isLoading.value = true;
        error.value = null;
        const currentUser = await userManager.getUser();
        if (currentUser && !currentUser.expired) {
          user.value = currentUser;
          return true;
        }
        return false;
      } catch (err) {
        error.value = err instanceof Error ? err : new Error('Check access failed');
        return false;
      } finally {
        isLoading.value = false;
      }
    }

    function clearError() {
      error.value = null;
    }

    syncUserFromStorage();

    return {
      user,
      isLoading,
      error,
      oidcIsAuthenticated,
      oidcAccessToken,
      oidcIdToken,
      signinRedirect,
      signinRedirectCallback,
      signoutRedirect,
      signoutRedirectCallback,
      getUser,
      removeUser,
      checkAccess,
      clearError,
      syncUserFromStorage,
    };
  },
  {
    persist: false,
  }
);

export default useOidcStore;
